import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Show, SignIn, SignUp, useAuth, UserButton } from "@clerk/react";
import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import { AppProvider, useApp } from "@/context/AppContext";
import { PhoneProvider } from "@/context/PhoneContext";
import { Layout } from "@/components/Layout";
import { setTokenGetter } from "@/lib/api";
import Dialer from "@/pages/Dialer";
import Contacts from "@/pages/Contacts";
import Scripts from "@/pages/Scripts";
import History from "@/pages/History";
import Settings from "@/pages/Settings";
import "@/App.css";

const AuthPage = ({ children }) => (
  <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
    <div className="hidden lg:flex flex-col justify-between bg-zinc-900 text-white p-12">
      <span className="font-heading text-xl font-bold">Phone Blower</span>
      <div className="space-y-3 max-w-md">
        <h1 className="font-heading text-4xl font-bold tracking-tight">Your calling floor, in one screen.</h1>
        <p className="text-zinc-400 text-sm">Live-agent and AI calling with compliance rules enforced on every call.</p>
      </div>
      <span className="text-xs text-zinc-500">Sign in with Google or email · authorized accounts only</span>
    </div>
    <div className="flex items-center justify-center p-6">{children}</div>
  </div>
);

const Pending = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
    <div className="max-w-md bg-white border border-slate-200 rounded-lg p-6 space-y-3" data-testid="pending-approval">
      <div className="flex justify-between items-center"><h1 className="font-heading text-xl font-bold">Waiting for approval</h1><UserButton /></div>
      <p className="text-sm text-slate-600">Your account was created. An admin needs to approve it in Settings → Team before you can use Phone Blower.</p>
    </div>
  </div>
);

const Member = () => {
  const { me, error } = useApp();
  if (error) return <p className="p-6 text-sm text-rose-700" data-testid="load-error">Could not load your account ({String(error)}). Refresh to retry.</p>;
  if (!me) return <p className="p-6 text-sm text-slate-500">Loading…</p>;
  if (me.role === "pending") return <Pending />;
  return (
    <PhoneProvider>
      <Layout>
        <Routes>
          <Route path="/" element={<Dialer />} />
          <Route path="/contacts" element={<Contacts />} />
          <Route path="/scripts" element={<Scripts />} />
          <Route path="/history" element={<History />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
    </PhoneProvider>
  );
};

const TokenBridge = ({ children }) => {
  const { getToken } = useAuth();
  setTokenGetter(getToken);
  useEffect(() => {
    setTokenGetter(getToken);
  }, [getToken]);
  return children;
};

const Protected = () => (
  <>
    <Show when="signed-in">
      <TokenBridge>
        <AppProvider><Member /></AppProvider>
      </TokenBridge>
    </Show>
    <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
  </>
);

export default function App() {
  return (
    <BrowserRouter>
      <ClerkAuthProvider>
        <Routes>
          <Route path="/sign-in/*" element={<AuthPage><SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" withSignUp /></AuthPage>} />
          <Route path="/sign-up/*" element={<AuthPage><SignUp routing="path" path="/sign-up" signInUrl="/sign-in" /></AuthPage>} />
          <Route path="/*" element={<Protected />} />
        </Routes>
      </ClerkAuthProvider>
      <Toaster position="bottom-right" richColors />
    </BrowserRouter>
  );
}
